<template>
  <div>
    <div class="mb-4 ml-1">
      <TCheckbox
        :model-value="isAllOfThisType"
        class="flex-row-reverse px-4 text-primary-950 md:flex-row md:px-0"
        :label="t(`searchFilters.rvType.${type}.selectAll`)"
        @update:model-value="selectAllRvs"
      />
    </div>

    <div class="grid md:grid-cols-2 gap-2 mb-2">
      <div
        v-for="rv in rvType"
        :key="rv"
       
      >
        <TCheckbox
          :id="`checkbox-${rv}`"
          :model-value="rvTypes.includes(rv)"
          :name="`checkbox-${rv}`"
          is-inverted
          is-centered
          class="p-4 w-full h-full border border-gray-200 text-primary-950 rounded-lg has-checked:border-primary-800 hover:bg-gray-50 has-focus:border-primary-800"
          @update:model-value="(value) => toggleType({ rvType: rv, value: Boolean(value) })"
        >
          <template #label>
            <div class="flex items-center gap-1">
              <TImage
                :src="getRvTypeIcon(rv)"
                :alt="iconAlt"
                :title="iconAlt"
                class="h-8 w-auto"
              />

              <div class="flex flex-col">
                <TBody
                  tag="span"
                  weight="semibold"
                >
                  {{ t(`rvType.${rv}`) }}
                </TBody>

                <TBody
                  tag="span"
                  size="sm"
                  variant="muted"
                >
                  {{ t(`rvTypeDescription.${rv}`) }}
                </TBody>
              </div>
            </div>
          </template>
        </TCheckbox>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type RV_TYPE_CATEGORY, type RV_TYPES, CATEGORIZED_RV_TYPES } from '~/constants/rv'

const props = defineProps<{
  type: RV_TYPE_CATEGORY
}>()

const model = defineModel<RV_TYPES[]>({ default: () => ([]) })

const { t } = useI18n()

const { getStaticImage } = useImage()
function getRvTypeIcon(rvType: string) {
  return getStaticImage({
    path: `/web-app/rv-type-icons/2025/${rvType}.svg`,
  })
}

const rvType = computed(() => {
  return CATEGORIZED_RV_TYPES[props.type]
})

const rvTypes = computed(() => model.value ?? [])

const isAllOfThisType = computed(() => {
  return rvType.value.every((rt) => rvTypes.value.includes(rt))
})

const translationKey = computed(() => {
  return props.type === 'towable' ? 'TravelTrailer' : 'Motorhome'
})

function selectAllRvs(value?: boolean) {
  // Types from the other category stay untouched so both categories can share one model.
  model.value = value
    ? [...rvTypes.value, ...rvType.value.filter((rt) => !rvTypes.value.includes(rt))]
    : rvTypes.value.filter((rt) => !rvType.value.includes(rt))
}

function toggleType({ rvType, value }: { rvType: RV_TYPES, value: boolean }) {
  if (value) {
    if (rvTypes.value.includes(rvType)) {
      return
    }

    model.value = [...rvTypes.value, rvType]
  }
  else {
    model.value = rvTypes.value.filter((rt) => rt !== rvType)
  }
}

const iconAlt = computed(() => {
  return t(`rvType.iconAlt`, {
    type: t(`rvType.${translationKey.value}`),
    rentalType: t(`rvType.${translationKey.value}`),
  })
})
</script>

<i18n src="~~/i18n/locales/common/search/filters/rv-types.json" lang="json" />
